import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { env } from '@/config/env';
import { translatePath } from '@/routes/paths';

/**
 * Canonical, og:url y alternativas de idioma de la pantalla actual (RNF-11).
 *
 * El fallo que arregla
 * --------------------
 * index.html trae estas etiquetas fijas apuntando a la portada en espanol, que
 * es lo unico que puede declarar un fichero estatico. Como la aplicacion es de
 * una sola pagina, TODAS las pantallas heredaban ese valor: rankings,
 * competiciones o "Quien soy" le decian a Google "la buena es /es". Con eso,
 * Google indexa la portada y descarta el resto por duplicado.
 *
 * Aqui se reescriben en cada navegacion con la direccion real de la pantalla.
 */

/**
 * Dominio con el que se construyen las direcciones.
 *
 * En produccion manda `VITE_SITE_URL`: la web tambien responde en la direccion
 * de Cloudflare, y las dos versiones tienen que declarar como buena la del
 * dominio propio. En desarrollo no hay dominio que valga, asi que se usa el
 * origen actual.
 */
function siteOrigin(): string {
  const configured = env.siteUrl.trim().replace(/\/+$/, '');
  if (/^https?:\/\//.test(configured) && !configured.includes('localhost')) return configured;
  return window.location.origin;
}

function setLink(rel: string, href: string, hreflang?: string): void {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let link = document.head.querySelector<HTMLLinkElement>(selector);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    if (hreflang) link.hreflang = hreflang;
    document.head.appendChild(link);
  }
  link.href = href;
}

function setMeta(property: string, content: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

export function useSeoTags(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const origin = siteOrigin();
    // Sin barra final: `/es/competiciones/` y `/es/competiciones` son la misma
    // pagina, y declarar dos formas distintas es crear el duplicado a mano.
    const path = pathname.replace(/\/+$/, '') || '/es';
    const url = `${origin}${path}`;

    setLink('canonical', url);
    setMeta('og:url', url);
    setLink('alternate', `${origin}${translatePath(path, 'es')}`, 'es');
    setLink('alternate', `${origin}${translatePath(path, 'en')}`, 'en');
    setLink('alternate', `${origin}${translatePath(path, 'en')}`, 'x-default');
  }, [pathname]);
}
